import { parsePermission, type PermissionType } from '@featit/shared/access-control';
import { isHttpAuthError, isRequestTimeoutError } from './ApiService';
import { FAILURE_REASONS, permissionReason } from './failureReasons';
import { GraphqlRequestError, failureReference, isReadTimeoutError } from './graphqlRequestError';
import { failures } from '@featit/shared/i18n/messages/en/failures';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { registerEnglishArea } from '@/i18n/catalogue';
import type { TFn } from '@/i18n/LocaleProvider';

registerEnglishArea('failures', failures);

/**
 * Why a read failed, in the three categories a reader can act on (I-0396).
 *
 * ⛔ **Never the transport's own sentence.** "Request failed with status code 500" and "Network Error"
 * are written for a network log; shown on a screen they explain nothing and suggest nothing. The
 * category says what kind of problem it is and what to do; the status and the raw message stay out of
 * the text a reader sees.
 *
 * Built on the checks `ApiService` already owns (`isHttpAuthError`, `isRequestTimeoutError`) and on
 * `GraphqlRequestError`'s kept `extensions`, so no transport rule is restated here.
 */
export type LoadFailureCategoryType = 'permission' | 'offline' | 'server' | 'unknown';

export interface ILoadFailure {
  category: LoadFailureCategoryType;
  /** The permission the deployment named in its refusal, when it named one it knows. */
  permission: PermissionType | null;
  /**
   * I-1470: the request id the server gave the failure, for a technical detail, never the headline.
   * Absent where it gave none. Never an operation, a field path or a route (EM-8).
   */
  reference?: string | null;
  /** RD-33: no answer came within the screen's deadline; the read may still be under way. */
  stalled?: boolean;
  /** I-2083: the server answered that it ran out of time (408); the deployment was reached. */
  serverTimedOut?: boolean;
  /**
   * I-1917: an address answered, but not with FEATit's API (a web page, a 405): the desktop app's
   * native requester could not read it as JSON and reports no status.
   */
  unreadableAnswer?: boolean;
}

/**
 * I-1917: the desktop's `CloudTransportError::InvalidResponse`, as `cloud_transport.rs` displays it.
 * A desktop command's failure reaches the web as its sentence alone (`toDesktopCommandError`), so
 * this sentence is the only mark it carries.
 */
export const DESKTOP_UNREADABLE_ANSWER = 'The cloud service returned an invalid response.';

function isDesktopUnreadableAnswer(error: unknown): boolean {
  const text = typeof error === 'string' ? error : (error as { message?: unknown } | null)?.message;
  return typeof text === 'string' && text.trim() === DESKTOP_UNREADABLE_ANSWER;
}

function statusOf(error: unknown): number | null {
  const response = (error as { response?: { status?: unknown } } | null)?.response;
  if (typeof response?.status === 'number') return response.status;
  // I-1917: the desktop app's cloud requester keeps the answer's status on the error itself
  // (`DesktopCloudTransportError`); matched by name, as `actionFailure` does, to import nothing.
  const desktop = error as { name?: unknown; status?: unknown } | null;
  if (desktop?.name === 'DesktopCloudTransportError' && typeof desktop.status === 'number') {
    return desktop.status;
  }
  if (error instanceof GraphqlRequestError) {
    const original = error.extensions.originalError as { statusCode?: unknown } | undefined;
    if (typeof original?.statusCode === 'number') return original.statusCode;
  }
  return null;
}

/**
 * The permission a refusal names. `AccessControlService.assert` writes it quoted — `does not hold
 * "work_area.read"` — and only a name the shared vocabulary knows is accepted, so a quoted fragment of
 * something else is never presented as a permission to ask for.
 */
function namedPermission(error: unknown): PermissionType | null {
  const message = (error as { message?: unknown } | null)?.message;
  const body = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data
    ?.message;
  for (const text of [body, message]) {
    if (typeof text !== 'string') continue;
    for (const match of text.matchAll(/"([a-z_]+\.[a-z_]+)"/g)) {
      const permission = parsePermission(match[1]);
      if (permission) return permission;
    }
  }
  return null;
}

export function describeLoadFailure(error: unknown): ILoadFailure {
  const failure = categoryOf(error);
  const reference = failureReference(error);
  return reference ? { ...failure, reference } : failure;
}

function categoryOf(error: unknown): ILoadFailure {
  const status = statusOf(error);
  const code = error instanceof GraphqlRequestError ? String(error.extensions.code ?? '') : '';
  if (isHttpAuthError(error) || status === 401 || status === 403 || code === 'FORBIDDEN') {
    return { category: 'permission', permission: namedPermission(error) };
  }
  const transport = error as {
    isAxiosError?: boolean;
    request?: unknown;
    response?: unknown;
  } | null;
  const deviceOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  // I-1829 (EM-20): a request that ran out its deadline online was sent and took too long — RD-33's
  // stalled read, not an unreachable deployment.
  if (isRequestTimeoutError(error) && !deviceOffline) {
    return { category: 'offline', permission: null, stalled: true };
  }
  // I-2083: a 408 is an answer. The deployment was reached and said it stopped the query, which is
  // neither "could not be reached" (offline) nor the app giving up on a request still out (stalled).
  if (isReadTimeoutError(error) && !isRequestTimeoutError(error)) {
    return { category: 'server', permission: null, serverTimedOut: true };
  }
  const noAnswer =
    isRequestTimeoutError(error) ||
    (Boolean(transport?.isAxiosError || transport?.request) && !transport?.response) ||
    deviceOffline;
  if (noAnswer) return { category: 'offline', permission: null };
  if (isDesktopUnreadableAnswer(error)) {
    return { category: 'server', permission: null, unreadableAnswer: true };
  }
  if ((status !== null && status >= 500) || code === 'INTERNAL_SERVER_ERROR') {
    return { category: 'server', permission: null };
  }
  return { category: 'unknown', permission: null };
}

/**
 * Whether a read failed because what it asked for is not there (I-0794): a GraphQL `NOT_FOUND`, or
 * a 404 from REST or folded into a GraphQL error. Not a failure to report at all — the address
 * names nothing, and the screen says so where the thing would have been, with no "Try again".
 */
export function isNotFoundFailure(error: unknown): boolean {
  if (error instanceof GraphqlRequestError && error.extensions.code === 'NOT_FOUND') return true;
  return statusOf(error) === 404;
}

/**
 * Whether a read failed because what it asked for was there and is gone for good (410): a delivery
 * revoked or expired, for one. Not a failure either — the screen says what happened, in plain words.
 */
export function isGoneFailure(error: unknown): boolean {
  return statusOf(error) === 410;
}

/** The sentence a reader gets for each category — what happened, and what to do about it. */
export function loadFailureReason(failure: ILoadFailure): string {
  switch (failure.category) {
    case 'permission':
      return failure.permission
        ? permissionReason(failure.permission)
        : FAILURE_REASONS.permissionUnnamed;
    case 'offline':
      return failure.stalled ? FAILURE_REASONS.stalled : FAILURE_REASONS.offline;
    case 'server':
      if (failure.unreadableAnswer) return FAILURE_REASONS.unreadableAnswer;
      return failure.serverTimedOut ? FAILURE_REASONS.serverTimeout : FAILURE_REASONS.server;
    default:
      return FAILURE_REASONS.unknown;
  }
}

export const LOAD_FAILURE_CATEGORY_LABELS: Record<LoadFailureCategoryType, string> = {
  permission: 'Permission',
  offline: 'Offline',
  server: 'Server error',
  unknown: 'Unexpected error',
};

/**
 * F-0621: `loadFailureReason` in the reader's language (`failures.reasons`), case for case; the
 * English function stays for the callers that have no page, and `loadFailure.i18n.test.ts` holds the
 * two to the same English.
 */
export function loadFailureReasonIn(t: TFn, failure: ILoadFailure): string {
  switch (failure.category) {
    case 'permission':
      return failure.permission
        ? t('failures.reasons.permission', { permission: failure.permission })
        : t('failures.reasons.permissionUnnamed');
    case 'offline':
      return t(failure.stalled ? 'failures.reasons.stalled' : 'failures.reasons.offline');
    case 'server':
      if (failure.unreadableAnswer) return t('failures.reasons.unreadableAnswer');
      return t(
        failure.serverTimedOut ? 'failures.reasons.serverTimeout' : 'failures.reasons.server',
      );
    default:
      return t('failures.reasons.unknown');
  }
}

const LOAD_FAILURE_LABEL_KEYS: Record<LoadFailureCategoryType, MessageKey> = {
  permission: 'failures.labels.permission',
  offline: 'failures.labels.offline',
  server: 'failures.labels.server',
  unknown: 'failures.labels.unknown',
};

/** A failed read's chip (`LOAD_FAILURE_CATEGORY_LABELS`) in the reader's language. */
export function loadFailureCategoryLabelIn(t: TFn, category: LoadFailureCategoryType): string {
  return t(LOAD_FAILURE_LABEL_KEYS[category]);
}
